// Lib
import React, { CSSProperties, RefObject } from 'react';

// Utils
import {
    getCaption,
    getElementId,
    getImageThumbnail,
    getIsTransparentImage,
    getShowCaption,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import { toCssUrl } from '../../../../../utils/cssUtil';

// Components
import Caption from '../../../../../components/caption/Caption';
import ElementImage from '../../../../../components/images/ElementImage';

// Constants
import { IMAGE_TYPES } from '../../../../../../common/media/mediaConstants';

import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

type ImageModalV2ContentProps = {
    element: ImMNElement;
    lowerQualityImageUrl?: string;
    isEditable: boolean;
    contentStyle: CSSProperties;
    imageWrapperStyle: CSSProperties;
    captionRef: RefObject<HTMLDivElement>;
};

/**
 * Renders the image filling the available space while maintaining aspect ratio.
 */
const ImageModalV2Content: React.FC<ImageModalV2ContentProps> = ({
    element,
    lowerQualityImageUrl,
    isEditable,
    contentStyle,
    imageWrapperStyle,
    captionRef,
}) => {
    const elementId = getElementId(element);
    const isTransparent = getIsTransparentImage(element);
    const imageThumbnailUrl = getImageThumbnail(element);
    const showCaption = getShowCaption(element);
    const imageBackgroundStyle =
        !isTransparent && imageThumbnailUrl ? { backgroundImage: toCssUrl(imageThumbnailUrl) } : undefined;

    return (
        <div className="ImageModalV2Content flex h-full w-full items-center justify-center">
            <div
                className="
                    ImageModalV2Content-image-wrapper flex flex-col overflow-hidden rounded-[0.5rem]
                    bg-[var(--ws-element-background-primary)]
                    [&_.Caption]:bg-[var(--ws-element-background-primary)] [&_.Caption]:px-6 [&_.Caption]:pt-4
                    [&_.CaptionTiptapEditor]:text-[var(--ui-foreground-secondary)]
                    [&_.CaptionTiptapEditor_.tiptap]:px-0
                "
                style={contentStyle}
            >
                <div
                    className="
                    ImageModalV2Content-image bg-cover bg-center bg-no-repeat
                    [&_.AttachmentImage]:h-full [&_.AttachmentImage]:w-full
                    [&_.AttachmentImage_img]:h-full [&_.AttachmentImage_img]:w-full [&_.AttachmentImage_img]:object-cover
                    [&_.BackgroundImage]:h-full [&_.BackgroundImage]:w-full
                    [&_.OptimisticImage]:h-full [&_.OptimisticImage]:w-full
                    [&_.ProgressiveImage]:h-full [&_.ProgressiveImage]:w-full
                    [&_.image-node]:block [&_.image-node]:h-full [&_.image-node]:w-full [&_.image-node]:object-cover [&_.image-node]:select-none
                    [&_img]:block [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:select-none
                "
                    style={{
                        ...imageBackgroundStyle,
                        ...imageWrapperStyle,
                    }}
                >
                    <ElementImage
                        useSecureMediaUrl
                        showBrokenIconOnError
                        element={element}
                        imageType={IMAGE_TYPES.ELEMENT}
                        widthPx={Number(imageWrapperStyle.width)}
                        thumbnailUrl={lowerQualityImageUrl}
                        displayFullSize
                        key={elementId}
                    />
                </div>
                {showCaption && (
                    <div ref={captionRef} className="ImageModalV2Content-caption flex-none">
                        <Caption
                            element={element}
                            textContent={getCaption(element)}
                            captionVisible={showCaption}
                            isEditable={isEditable}
                            isModalView
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageModalV2Content;
